<template>
  <v-card tile flat color="transparent" :style="CSS_VARS" class="ecs-treeselect-container">
    <treeselect
      ref="treeSelect"
      v-model="updateValue"
      :clearable="clearable"
      :close-on-select="closeOnSelect"
      :disabled="disabled"
      :disable-branch-nodes="disableBranchNodes"
      :max-height="maxHeight"
      :options="items"
      :open-on-click="openOnClick"
      :placeholder="placeholder"
      :searchable="searchable"
      :tab-index="Number(tabindex)"
      @close="onClose"
    >
      <div slot="value-label" slot-scope="{ node }">
        <slot name="value-label" :node="node" />
      </div>
    </treeselect>

    <div v-if="showError" class="v-text-field__details" style="margin-top: 16px; margin-left: 12px;">
      <div class="v-messages error--text">
        <div class="v-messages__wrapper">
          <div class="v-messages__message">
            {{ errorBucket[0] }}
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import Validatable from 'vuetify/lib/mixins/validatable';

export default {
  name: 'EcsTreeSelect',

  components: { Treeselect },

  mixins: [Validatable],

  props: {
    clearable: {
      type: Boolean,
      default: false
    },
    closeOnSelect: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disableBranchNodes: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default() {
        return [];
      }
    },
    maxHeight: {
      type: Number,
      default: 300
    },
    openOnClick: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    rules: {
      type: Array,
      default() {
        return [];
      }
    },
    searchable: {
      type: Boolean,
      default: false
    },
    tabindex: {
      type: String,
      default: '0'
    },
    value: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      listenerFn: e => {
        if (e.key === 'Tab') {
          e.stopPropagation();
        }
      }
    };
  },

  computed: {
    updateValue: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.internalValue = newValue;
        this.$emit('update:value', newValue);
        this.$emit('change', newValue);
      }
    },
    CSS_VARS() {
      return {
        '--font-color': this.$vuetify.theme.dark ? '#ffffff' : '#111111',
        '--background-color': 'var(--v-surface-base)'
      };
    },
    showError() {
      return this.hasInput && this.hasError;
    }
  },

  mounted() {
    const element = this.searchable
      ? this.$refs.treeSelect.$el.querySelector('.vue-treeselect__input')
      : this.$refs.treeSelect.$el.querySelector('.vue-treeselect__input-container');

    if (element) {
      element.onfocus = this.onFocus;
      element.onblur = this.onBlur;
    }
  },

  methods: {
    onBlur() {
      this.hasInput = true;
      this.$refs.treeSelect &&
        this.$refs.treeSelect.$el &&
        this.$refs.treeSelect.$el.removeEventListener('keydown', this.listenerFn, true);
      this.$emit('blur');
    },

    onClose() {
      if (this.searchable) {
        setTimeout(() => {
          this.$refs.treeSelect.$el.querySelector('.vue-treeselect__input').focus();
        }, 10);
      }
    },

    onFocus() {
      // AWC-130 This is to force the treeselect to stop opening on 'Tab'
      this.$refs.treeSelect.$el.addEventListener('keydown', this.listenerFn, true);
      this.$emit('focus');
    }
  }
};
</script>

<style>
.ecs-treeselect-container {
  margin-top: -5px;
}

.vue-treeselect__menu {
  background-color: var(--background-color);
  border-color: var(--v-border-color);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #b8b8b8 #4d4d4d;
}

.vue-treeselect__menu::-webkit-scrollbar-track {
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  border-radius: 6px;
}
.vue-treeselect__menu::-webkit-scrollbar {
  width: 6px;
  height: 6px;
  background-color: transparent;
}
.vue-treeselect__menu::-webkit-scrollbar-thumb {
  border-radius: 6px;
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #cccccc;
}

.vue-treeselect__menu::-webkit-scrollbar-corner {
  background: transparent;
}

.vue-treeselect__control {
  background: none !important;
  border: none;
  width: auto;
}
/* Push the arrow to the left a little so it aligns with other dropdowns */
.vue-treeselect__control-arrow-container {
  padding-right: 14px;
}
/* Remove the subtle focus color */
.vue-treeselect--focused:not(.vue-treeselect--open) .vue-treeselect__control {
  border: none;
  box-shadow: none;
}

/* Make all option arrows the same color */
.vue-treeselect__option-arrow,
.vue-treeselect__control-arrow {
  color: var(--font-color);
}

.vue-treeselect__option:hover .vue-treeselect__control-arrow {
  color: #fff;
}

/* Use our apps font color for menu options and selected unfocused input */
.vue-treeselect__option,
.vue-treeselect__single-value,
.vue-treeselect--focused.vue-treeselect--searchable .vue-treeselect__single-value {
  color: var(--font-color);
}

/* Highlight highlighted/selection option with app primary color */
.vue-treeselect__option--selected {
  background: var(--v-primary-lighten1) !important;
}

.vue-treeselect__option--selected {
  color: #fff;
}

.vue-treeselect__option--highlight,
.vue-treeselect__option:hover {
  background: #0002;
}

/* Don't show pointer or highlight for disabled options */
.vue-treeselect__option.vue-treeselect__option--disabled.vue-treeselect__option--highlight {
  pointer-events: none;
  background: none !important;
}

/* Don't show arrow when control is 'disabled' */
.vue-treeselect--disabled .vue-treeselect__control-arrow-container {
  visibility: hidden;
}

/* Make disabled arrow slightly transparent, for that 'disabled' look */
.vue-treeselect__option.vue-treeselect__option--disabled > .vue-treeselect__option-arrow-container {
  opacity: 0.5;
}

/* Make disabled option text slightly transparent, but don't change color */
.vue-treeselect__option--disabled .vue-treeselect__label-container {
  opacity: 0.5;
  color: var(--font-color);
}

/* Keep arrow color consistent when disabled */
.vue-treeselect--branch-nodes-disabled .vue-treeselect__option:hover .vue-treeselect__option-arrow {
  color: var(--font-color);
}

.ecs-treeselect-container .vue-treeselect__x-container {
  padding: 8px 16px;
}
</style>
